import type { ReactElement } from 'react';
import { useState } from 'react';
import type { ZipBoundary } from '@shared/api';
import { PageShell, PageTitle } from '../Page';
import type { PageProps } from '../types';
import { marketPeopleCount, marketRadiusMiles, pickGeoLayout, targetAreas } from '@shared/geo-layout';
import type { FitPad } from './GeoMapCanvas';
import { GeoMapCanvas } from './GeoMapCanvas';
import { MarketMap } from './MarketMap';
import { Frame } from './primitives';

/**
 * geo_strategy (brief §4 `08 geo`): FULL-BLEED light - the map fills the
 * card to the rounded edge (`Frame`'s own `overflow: hidden` on `.mv3-card`
 * already clips it, same rule as a photo slot). Reuses `GeoMapCanvas`
 * (design.md V8) by import rather than forking it: `appearance="light"`
 * picks its existing light/muted map styling
 * (`web/src/lib/theme.ts`-independent, built into the component already),
 * and `centerBiasFraction={0.12}` is the one additive prop this stream added
 * to it for the brief's "offset the map centre ~12% right of card centre"
 * (v1's own geo page never passes it, so its ~16% default is untouched).
 * Floating on top: an h1 title top-left and the slide's one small map-data
 * credit bottom-right (OSM's licence needs it visible; one per slide, not a
 * chip per map). There is no body text card on any geo layout. The mint
 * radius ring is drawn by `GeoMapCanvas` itself, restyled for the light surface in `momentum-v3-misc.css` (every
 * `.mtm-geo-*` class GeoMapCanvas renders is styled there under
 * `.tpl-momentum-v3`, alongside momentum_v1's own `.tpl-momentum` copy in
 * styles.css - disjoint ancestors, so neither ruleset touches the other).
 */
export function GeoStrategyPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const { geography, advertiser } = proposal;
  const [, setAutoZips] = useState<ZipBoundary[] | null>(null);
  const layout = pickGeoLayout(geography.strategy, targetAreas(geography).length);

  if (layout.kind === 'national' || layout.kind === 'state') {
    return <FullBleedMapPage proposal={proposal} section={section} actions={actions} readOnly={readOnly} />;
  }
  if (layout.kind !== 'single') {
    return <MultiMarketPage proposal={proposal} section={section} actions={actions} readOnly={readOnly} />;
  }

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-geo-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-geo-canvas-wrap">
          <GeoMapCanvas
            advertiser={advertiser}
            geography={geography}
            readOnly={readOnly}
            appearance="light"
            centerBiasFraction={0.12}
            onZipsChange={setAutoZips}
            onToggleZip={(nextZipCodes) => actions.setGeography({ zipCodes: nextZipCodes }, 'Updated ZIP targeting')}
          />

          <div className="mv3-geo-topbar">
            <PageTitle
              title={section.title}
              readOnly={readOnly}
              onSave={(title) => actions.saveTitle(section, title)}
              className="mv3-geo-title"
            />
          </div>

          <span className="mv3-geo-credit">Map data © OpenStreetMap contributors</span>
        </div>
      </Frame>
    </PageShell>
  );
}



// ---------------------------------------------------------------------------
// Full-bleed target maps: national (fitted to the contiguous US) and one
// state (fitted to the state). Same shape as the original single-location
// slide: detailed map to the card edge, title floating top-left, no text
// card or footer. The target is tinted + outlined; priority markets (or a
// state's metros) are pinned and labelled on the map.
// ---------------------------------------------------------------------------

const count = (n: number): string => Math.round(n).toLocaleString('en-US');

/** "2.07M", "182K": a reach figure for a headline, never rounded below what is known. */
function compactCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}K`;
  return count(n);
}

const NATIONAL_PAD: FitPad = { top: 170, right: 70, bottom: 50, left: 70 };
const STATE_PAD: FitPad = { top: 190, right: 60, bottom: 50, left: 60 };

function FullBleedMapPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const { geography, audience } = proposal;
  const national = geography.strategy === 'national';
  const stateArea = national ? null : targetAreas(geography)[0] ?? null;
  const pinAreas = national ? geography.areas : geography.areas.filter((a) => a.kind !== 'state');
  const pins = pinAreas.map((area) => {
    const people = marketPeopleCount(audience.segments, area.label);
    return { label: area.label, center: area.center, tag: people !== null ? `${area.label}  ${count(people)}` : area.label };
  });
  const total = audience.estimatedPeople?.value ?? null;
  const chip = national ? 'Nationwide' : (stateArea?.label ?? '');

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-geo-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-geo-canvas-wrap">
          {national ? (
            <MarketMap className="mv3-geo-full-map" view={{ kind: 'us' }} tint="us" pad={NATIONAL_PAD} pins={pins} />
          ) : stateArea ? (
            <MarketMap
              className="mv3-geo-full-map"
              view={{ kind: 'states', names: [stateArea.label] }}
              tint={[stateArea.label]}
              pad={STATE_PAD}
              pins={pins}
            />
          ) : null}

          <div className="mv3-geo-topbar mv3-geo-topbar-wide">
            <div className="mv3-geo-titlerow">
              <PageTitle
                title={section.title}
                readOnly={readOnly}
                onSave={(title) => actions.saveTitle(section, title)}
                className="mv3-geo-title mv3-geo-title-wide"
              />
              {total !== null ? (
                <div className="mv3-geo-reach">
                  <strong>{compactCount(total)}</strong>
                  <span>people</span>
                </div>
              ) : null}
            </div>
            {chip ? <span className="mv3-geo-nationwide-chip">{chip}</span> : null}
          </div>

          <span className="mv3-geo-credit">Map data © OpenStreetMap contributors</span>
        </div>
      </Frame>
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// Multi-market layouts. The layout is a pure function of the target count
// (src/domain/geo-layout.ts) - columns for 2/3/5, a grid for 4/6, and one US
// map + ranked list for 7+ (metros or states).
// ---------------------------------------------------------------------------

function MultiMarketPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const { geography, audience } = proposal;
  const areas = targetAreas(geography);
  const layout = pickGeoLayout(geography.strategy, areas.length);
  const markets = areas.map((area) => ({
    name: area.label,
    center: area.center,
    people: marketPeopleCount(audience.segments, area.label),
    radiusMiles: marketRadiusMiles(area, geography, audience.segments),
  }));

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-geo-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className={`mv3-geo-multi mv3-geo-multi-${layout.kind}`}>
          <div className="mv3-geo-multi-head">
            <PageTitle
              title={section.title}
              readOnly={readOnly}
              onSave={(title) => actions.saveTitle(section, title)}
              className="mv3-geo-title mv3-geo-title-multi"
            />
          </div>

          {layout.kind === 'map-list' ? (
            <div className="mv3-geo-maplist">
              <MarketMap
                className="mv3-geo-us-map"
                view={{ kind: 'us' }}
                tint={layout.byState ? markets.map((m) => m.name) : undefined}
                pad={{ top: 20, right: 20, bottom: 20, left: 20 }}
                pins={layout.byState ? [] : markets.map((m, i) => ({ label: m.name, center: m.center, badge: String(i + 1) }))}
              />
              <div className="mv3-geo-ranked">
                <div className="mv3-geo-ranked-head">
                  <div className="mv3-geo-ranked-kicker">
                    {markets.length} {layout.byState ? 'states' : 'markets'}
                  </div>
                </div>
                <ol className={`mv3-geo-ranked-list${markets.length > 9 ? ' mv3-geo-ranked-list-dense' : ''}`}>
                  {markets.map((m, i) => (
                    <li key={m.name} className="mv3-geo-ranked-item">
                      <span className="mv3-geo-rank">{i + 1}</span>
                      <span className="mv3-geo-ranked-name">{m.name}</span>
                      {m.people !== null ? <span className="mv3-geo-ranked-count">{count(m.people)}</span> : null}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          ) : (
            <div
              className={`mv3-geo-tiles mv3-geo-tiles-${layout.kind}${layout.columns >= 5 ? ' mv3-geo-tiles-narrow' : ''}`}
              style={{
                gridTemplateColumns: `repeat(${layout.columns}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${layout.rows}, minmax(0, 1fr))`,
              }}
            >
              {markets.map((m) => (
                <div key={m.name} className="mv3-geo-tile">
                  {layout.byState ? (
                    <MarketMap
                      className="mv3-geo-tile-map"
                      view={{ kind: 'states', names: [m.name] }}
                      tint={[m.name]}
                      pad={{ top: 30, right: 30, bottom: layout.kind === 'grid' ? 90 : 30, left: 30 }}
                      pins={[]}
                    />
                  ) : (
                    <MarketMap
                      className="mv3-geo-tile-map"
                      view={{ kind: 'radius', miles: m.radiusMiles }}
                      pins={[{ label: m.name, center: m.center }]}
                    />
                  )}
                  <div className="mv3-geo-tile-caption">
                    <div className="mv3-geo-tile-name">{m.name}</div>
                    {m.people !== null ? (
                      <div className="mv3-geo-tile-count">
                        <strong>{count(m.people)}</strong> people
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}

          <span className="mv3-geo-credit">Map data © OpenStreetMap contributors</span>
        </div>
      </Frame>
    </PageShell>
  );
}
